import React, { useEffect, useState } from "react";
import API from "../services/api";

const CancelledBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Details Modal States
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bids, setBids] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchCancelledBookings = async () => {
    try {
      setLoading(true);
      const res = await API.get("/cancelled-bookings");
      if (res.data.success) {
        setBookings(res.data.bookings);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingDetails = async (bookingId) => {
    try {
      setDetailLoading(true);
      const res = await API.get(`/booking/${bookingId}`);
      if (res.data.success) {
        setSelectedBooking(res.data.booking);
        setBids(res.data.bids);
        setIsModalOpen(true);
      }
    } catch (err) {
      alert("Failed to fetch details");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchCancelledBookings();
  }, []);

  if (loading) return <div style={styles.loading}>Loading cancelled bookings...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Cancelled Bookings</h2>
          <p style={styles.subtitle}>Manage all cancelled ride requests</p>
        </div>
        <div style={styles.badgeCount}>{bookings.length} Total</div>
      </div>

      {/* GRID */}
      {bookings.length === 0 ? (
        <div style={styles.empty}>No cancelled bookings found</div>
      ) : (
        <div style={styles.grid}>
          {bookings.map((b) => (
            <div 
              key={b._id} 
              style={styles.card} 
              onClick={() => fetchBookingDetails(b._id)}
            >
              <div style={styles.cardTop}>
                <span style={styles.bookingId}>{b._id.slice(-6).toUpperCase()}</span>
                <span style={styles.cancelBadge}>{b.status}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>User</span>
                <span style={styles.value}>{b.user?.name || "N/A"}</span>
              </div>

              <div style={styles.row}>
                <span style={styles.label}>Driver</span>
                <span style={styles.value}>{b.assignedDriver?.name || "Not Assigned"}</span>
              </div>

              <div style={styles.footer}>
                {new Date(b.createdAt).toLocaleString()}
                {detailLoading && " • Loading..."}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* DETAILS MODAL */}
      {isModalOpen && selectedBooking && (
        <div style={styles.overlay} onClick={() => setIsModalOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3>Booking {selectedBooking._id.slice(-6).toUpperCase()}</h3>
              <button style={styles.closeBtn} onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            <div style={styles.modalBody}>
              <section style={styles.section}>
                <h4 style={styles.sectionTitle}>User Info</h4>
                <p style={styles.modalText}><strong>Name:</strong> {selectedBooking.user?.name}</p>
                <p style={styles.modalText}><strong>Phone:</strong> {selectedBooking.user?.phone}</p>
              </section>

              <section style={styles.section}>
                <h4 style={styles.sectionTitle}>Bidding History ({bids.length})</h4>
                {bids.length === 0 ? (
                  <p style={styles.label}>No bids were placed on this booking.</p>
                ) : (
                  <div style={styles.bidList}>
                    {bids.map((bid) => (
                      <div key={bid._id} style={styles.bidItem}>
                        <span>{bid.driver?.name}</span>
                        <span style={styles.bidAmount}>₹{bid.amount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= STYLES ================= */

const styles = {
  // ... Your existing styles ...
  page: { padding: "20px", background: "#f5f7fb", minHeight: "100vh", fontFamily: "Arial" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "25px" },
  title: { margin: 0, fontSize: "24px" },
  subtitle: { margin: "4px 0 0 0", fontSize: "13px", color: "#6b7280" },
  badgeCount: { background: "#ef4444", color: "white", padding: "8px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: "bold" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "15px" },
  card: { background: "#fff", borderRadius: "14px", padding: "15px", boxShadow: "0 4px 15px rgba(0,0,0,0.06)", cursor: "pointer" },
  cardTop: { display: "flex", justifyContent: "space-between", marginBottom: "12px" },
  bookingId: { fontFamily: "monospace", fontSize: "13px", color: "#374151" },
  cancelBadge: { background: "#fee2e2", color: "#b91c1c", padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "bold" },
  row: { display: "flex", justifyContent: "space-between", marginBottom: "8px" },
  label: { fontSize: "12px", color: "#6b7280" },
  value: { fontSize: "13px", fontWeight: "500", color: "#111827" },
  footer: { marginTop: "10px", fontSize: "11px", color: "#9ca3af", borderTop: "1px solid #eee", paddingTop: "8px" },
  loading: { padding: "20px" },
  error: { color: "red", padding: "20px" },
  empty: { textAlign: "center", padding: "40px", color: "#6b7280" },

  // Modal Styles
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 100 },
  modal: { background: "#fff", width: "90%", maxWidth: "500px", borderRadius: "16px", padding: "20px", boxShadow: "0 10px 25px rgba(0,0,0,0.2)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: "10px", marginBottom: "15px" },
  closeBtn: { background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#999" },
  section: { marginBottom: "20px" },
  sectionTitle: { fontSize: "14px", color: "#374151", marginBottom: "10px", borderLeft: "4px solid #ef4444", paddingLeft: "8px" },
  modalText: { fontSize: "13px", margin: "5px 0", color: "#4b5563" },
  bidList: { background: "#f9fafb", borderRadius: "8px", padding: "10px" },
  bidItem: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #edf2f7", fontSize: "12px" },
  bidAmount: { fontWeight: "bold", color: "#059669" }
};

export default CancelledBookings;